import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateDesignPreview } from '../utils/designPreview';
import { compressImageToFile, THUMBNAIL_DIMENSIONS, revokeAllFileUrls } from '../utils/imageCompression';
import { uploadImageToS3, uploadDesignToS3, uploadPreviewImagesToS3, getContentTypeFromExtension, detectFileExtensionFromUrl } from '../utils/s3Storage';

export const CartContext = createContext();

const CART_STORAGE_KEY = 'dekalio_cart';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [designStates, setDesignStates] = useState({});
  
  // Load cart from localStorage on initial render
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('dekalio_cart');
      const savedDesignStates = localStorage.getItem('dekalio_design_states');
      
      if (savedCart) {
        setCart(JSON.parse(savedCart));
      } else {
        setCart({ items: [], total: 0 });
      }
      
      if (savedDesignStates) {
        setDesignStates(JSON.parse(savedDesignStates));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
      setCart({ items: [], total: 0 });
    }
  }, []);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cart) {
      try {
        localStorage.setItem('dekalio_cart', JSON.stringify(cart));
      } catch (error) {
        // Check if it's a quota exceeded error
        if (error.name === 'QuotaExceededError' || 
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            error.code === 22) {
          console.error('localStorage quota exceeded! Cart data is too large:', 
            JSON.stringify(cart).length / 1024, 'KB');
          
          // You could implement a fallback here, like removing preview images
          // or other large data from the cart before trying to save again
        } else {
          console.error('Error saving cart to localStorage:', error);
        }
      }
    }
  }, [cart]);
  
  // Save design states to localStorage
  useEffect(() => {
    if (Object.keys(designStates).length > 0) {
      try {
        localStorage.setItem('dekalio_design_states', JSON.stringify(designStates));
      } catch (error) {
        // Check if it's a quota exceeded error
        if (error.name === 'QuotaExceededError' || 
            error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            error.code === 22) {
          console.error('localStorage quota exceeded! Design states data is too large:', 
            JSON.stringify(designStates).length / 1024, 'KB');
          
          // You could implement a fallback here
        } else {
          console.error('Error saving design states to localStorage:', error);
        }
      }
    }
  }, [designStates]);

  // Clean up file URLs when component unmounts
  useEffect(() => {
    return () => {
      revokeAllFileUrls();
    };
  }, []);

  const addToCart = async (item) => {
    try {
      // Generate a unique ID for this cart item
      const cartItemId = uuidv4();
      const designS3Key = `designs/${cartItemId}`;
      
      // Convert all blob URLs to S3 URLs for storage
      const processedDesign = {};
      for (const [view, graphics] of Object.entries(item.design)) {
        processedDesign[view] = await Promise.all(graphics.map(async (graphic, index) => {
          if (graphic.type === 'image') {
            // Upload image to S3
            // Preserve original file extension from graphic.name or use fallback
            const originalName = graphic.name || 'image';
            const fileExtension = originalName.includes('.') ? 
              originalName.substring(originalName.lastIndexOf('.')) : '.png';
            const baseName = originalName.includes('.') ? 
              originalName.substring(0, originalName.lastIndexOf('.')) : originalName;
            const imageKey = `${designS3Key}/${view}/${index}_${baseName}${fileExtension}`;
            
            // Determine content type based on file extension
            const contentType = getContentTypeFromExtension(fileExtension);
            
            const s3Url = await uploadImageToS3(graphic.src, imageKey, contentType);
            return { ...graphic, src: s3Url };
          }
          return graphic;
        }));
      }

      // Upload all preview images to S3
      const previewImagesS3Key = `designs/${cartItemId}/previews`;
      let s3PreviewImages = {};
      
      if (item.previewImages) {
        s3PreviewImages = await uploadPreviewImagesToS3(item.previewImages, previewImagesS3Key);
        console.log('Uploaded preview images with keys:', Object.keys(s3PreviewImages));
      } else if (item.previewImage) {
        // If only a single preview image is available
        const detectedExtension = detectFileExtensionFromUrl(item.previewImage);
        const mainPreviewKey = `${previewImagesS3Key}/main${detectedExtension}`;
        const contentType = getContentTypeFromExtension(detectedExtension);
        const s3MainPreviewUrl = await uploadImageToS3(item.previewImage, mainPreviewKey, contentType);
        s3PreviewImages = { main: s3MainPreviewUrl };
      }

      // Upload the design data to S3
      const designDataKey = `${designS3Key}/design.json`;
      await uploadDesignToS3(processedDesign, designDataKey);

      // Use the "Sida vänster" S3 URL directly as the thumbnail instead of creating a blob
      let thumbnailUrl = null;
      // First check for "Sida vänster" with proper case
      if (s3PreviewImages && s3PreviewImages['Sida vänster']) {
        thumbnailUrl = s3PreviewImages['Sida vänster'];
      } else {
        // If not found, check for any variation of "sida vänster" case-insensitively
        const previewKeys = Object.keys(s3PreviewImages || {});
        const sidaVansterKey = previewKeys.find(key => 
          key.toLowerCase().includes('sida') && key.toLowerCase().includes('vänster'));
        
        if (sidaVansterKey) {
          thumbnailUrl = s3PreviewImages[sidaVansterKey];
        } else if (Object.keys(s3PreviewImages || {}).length > 0) {
          // Fall back to any available preview image
          thumbnailUrl = Object.values(s3PreviewImages)[0];
        } else {
          // Last resort - create a standardized thumbnail from the provided preview image
          const compressedThumbnail = await compressImageToFile(
            item.previewImage, 
            0.5,  // 500KB max size
            THUMBNAIL_DIMENSIONS.width,  // Use standard width
            true  // Standardize the aspect ratio
          );
          thumbnailUrl = compressedThumbnail; // This will be a blob URL
        }
      }

      const cartItem = {
        ...item,
        id: cartItemId,
        previewImage: thumbnailUrl, // Use the S3 URL directly
        previewImages: Object.keys(s3PreviewImages).length > 0 ? s3PreviewImages : undefined, // S3 URLs
        s3Data: {
          designKey: designDataKey,
          previewImagesKey: previewImagesS3Key
        },
        design: processedDesign,
        designState: {
          selectedColor: item.selectedColor || '#FFFFFF',
          helfoliering: item.helfoliering === true,
          placedGraphics: processedDesign,
          selectedCar: item.car,
          uploadedFiles: Object.values(processedDesign)
            .flat()
            .filter(graphic => graphic.type === 'image')
            .map(graphic => ({
              id: graphic.originalFileId,
              name: graphic.name,
              src: graphic.src,
              needsVectorization: graphic.needsVectorization
            }))
        }
      };
      
      setCart(prevCart => ({
        items: [...prevCart.items, cartItem],
        total: prevCart.items.reduce((sum, item) => sum + item.totalCost, 0) + cartItem.totalCost
      }));
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  const updateCartItem = async (itemId, updatedItem) => {
    try {
      const designS3Key = `designs/${itemId}`;
      
      // Process the design to upload images to S3
      const processedDesign = {};
      for (const [view, graphics] of Object.entries(updatedItem.design)) {
        processedDesign[view] = await Promise.all(graphics.map(async (graphic, index) => {
          if (graphic.type === 'image') {
            // Only upload to S3 if it's a blob URL (new image)
            if (graphic.src.startsWith('blob:')) {
              // Preserve original file extension from graphic.name or use fallback
              const originalName = graphic.name || 'image';
              const fileExtension = originalName.includes('.') ? 
                originalName.substring(originalName.lastIndexOf('.')) : '.png';
              const baseName = originalName.includes('.') ? 
                originalName.substring(0, originalName.lastIndexOf('.')) : originalName;
              const imageKey = `${designS3Key}/${view}/${index}_${baseName}${fileExtension}`;
              
              // Determine content type based on file extension
              const contentType = getContentTypeFromExtension(fileExtension);
              
              const s3Url = await uploadImageToS3(graphic.src, imageKey, contentType);
              return { ...graphic, src: s3Url };
            }
          }
          return graphic;
        }));
      }
      
      // Update preview images in S3
      const previewImagesS3Key = `designs/${itemId}/previews`;
      let s3PreviewImages = {};
      
      if (updatedItem.previewImages) {
        s3PreviewImages = await uploadPreviewImagesToS3(updatedItem.previewImages, previewImagesS3Key);
        console.log('Updated preview images with keys:', Object.keys(s3PreviewImages));
      } else if (updatedItem.previewImage) {
        const detectedExtension = detectFileExtensionFromUrl(updatedItem.previewImage);
        const mainPreviewKey = `${previewImagesS3Key}/main${detectedExtension}`;
        const contentType = getContentTypeFromExtension(detectedExtension);
        const s3MainPreviewUrl = await uploadImageToS3(updatedItem.previewImage, mainPreviewKey, contentType);
        s3PreviewImages = { main: s3MainPreviewUrl };
      }
      
      // Upload updated design data to S3
      const designDataKey = `${designS3Key}/design.json`;
      await uploadDesignToS3(processedDesign, designDataKey);
      
      // Use the "Sida vänster" S3 URL directly as the thumbnail instead of creating a blob
      let thumbnailUrl = null;
      // First check for "Sida vänster" with proper case
      if (s3PreviewImages && s3PreviewImages['Sida vänster']) {
        thumbnailUrl = s3PreviewImages['Sida vänster'];
      } else {
        // If not found, check for any variation of "sida vänster" case-insensitively
        const previewKeys = Object.keys(s3PreviewImages || {});
        const sidaVansterKey = previewKeys.find(key => 
          key.toLowerCase().includes('sida') && key.toLowerCase().includes('vänster'));
        
        if (sidaVansterKey) {
          thumbnailUrl = s3PreviewImages[sidaVansterKey];
        } else if (Object.keys(s3PreviewImages || {}).length > 0) {
          // Fall back to any available preview image
          thumbnailUrl = Object.values(s3PreviewImages)[0];
        } else {
          // Last resort - create a standardized thumbnail from the provided preview image
          const compressedThumbnail = await compressImageToFile(
            updatedItem.previewImage, 
            0.5,  // 500KB max size
            THUMBNAIL_DIMENSIONS.width,  // Use standard width
            true  // Standardize the aspect ratio
          );
          thumbnailUrl = compressedThumbnail; // This will be a blob URL
        }
      }

      setCart(prevCart => {
        const updatedItems = prevCart.items.map(item => 
          item.id === itemId ? {
            ...item,
            ...updatedItem,
            previewImage: thumbnailUrl,
            previewImages: s3PreviewImages,
            s3Data: {
              designKey: designDataKey,
              previewImagesKey: previewImagesS3Key
            },
            design: processedDesign,
            designState: {
              selectedColor: updatedItem.selectedColor || '#FFFFFF',
              helfoliering: updatedItem.helfoliering === true,
              placedGraphics: processedDesign,
              selectedCar: updatedItem.car,
              uploadedFiles: Object.values(processedDesign)
                .flat()
                .filter(graphic => graphic.type === 'image')
                .map(graphic => ({
                  id: graphic.originalFileId,
                  name: graphic.name,
                  src: graphic.src,
                  needsVectorization: graphic.needsVectorization
                }))
            }
          } : item
        );
        
        const updatedTotal = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
        
        return {
          items: updatedItems,
          total: updatedTotal
        };
      });
    } catch (error) {
      console.error('Error updating cart item:', error);
    }
  };

  const clearCart = () => {
    setCart({
      items: [],
      total: 0
    });
  };

  const getDesignState = (itemId) => {
    const item = cart?.items?.find((i) => i.id === itemId);
    return item?.designState ?? null;
  };

  const removeFromCart = (itemId) => {
    setCart(prevCart => {
      // Log for debugging
      console.log('Removing item:', itemId);
      console.log('Current items:', prevCart.items);
      
      // Make sure we're matching the correct item
      const updatedItems = prevCart.items.filter(item => {
        // Log each item's ID for comparison
        console.log('Comparing:', item.id, itemId);
        return item.id !== itemId;
      });
      
      // Calculate new total only from remaining items
      const updatedTotal = updatedItems.reduce((sum, item) => sum + item.totalCost, 0);
      
      // Log the result
      console.log('Updated items:', updatedItems);
      
      return {
        items: updatedItems,
        total: updatedTotal
      };
    });
  };

  return (
    <CartContext.Provider value={{ 
      cart, 
      addToCart, 
      updateCartItem,
      removeFromCart,
      clearCart,
      getDesignState,
      designStates,
      setDesignStates
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext; 